import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import { Box, Divider, Stack, Typography } from '@mui/material';
import ActionButton from 'common/components/ActionButton';
import CustomIconButton from 'common/components/CustomIconButton';
import CustomTooltip from 'common/components/CustomTooltip';
import { LayoutDetails } from 'common/layouts/LayoutDetails';
import config from 'config';
import { useSnackbar } from 'notistack';
import { useCallback, useMemo } from 'react';
import CopyToClipboard from 'react-copy-to-clipboard';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { getOrganization } from 'redux/slices/organizationSlice';
import { getLanguage, getSettings } from 'redux/slices/settingSlice';
import { getTemplateTypes } from 'redux/slices/templateTypeSlice';
import SettingsKey from 'app/profile/settings/utils/settingsTypes';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()((theme) => {
    return {
        small: {
            width: 'auto',
            minWidth: theme.spacing(60),
            padding: '16px 32px 42px',

            [theme.breakpoints.up('xl')]: {
                width: 'auto',
            },
            [theme.breakpoints.down('mg')]: {
                width: '80vw',
                minWidth: 'auto',
            },
            [theme.breakpoints.down('ms')]: {
                width: '80vw',
                minWidth: 'auto',
            },
        },
        linkSection: {
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: theme.spacing(1),
            padding: theme.spacing(2),
            backgroundColor: theme.palette.background.paper,
        },
        linkTitle: {
            marginBottom: theme.spacing(1),
            color: theme.palette.text.secondary,
            fontSize: '0.875rem',
        },
    };
});

/**
 * Enhanced ServiceShare component that provides multiple link options
 * based on service properties:
 *
 * 1. **Original Link**: Includes serviceId (if no type) + dealer/group
 * 2. **Type Only Link**: Only includes service type (when type exists)
 * 3. **Dealer Only Link**: Only includes dealer/group (without serviceId or type)
 * 4. **Iframe Code**: For embedding in other pages
 */
const ServiceShare = ({ template, group }) => {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const { classes } = useStyles();
    const lang = useSelector(getLanguage);
    const organization = useSelector(getOrganization);
    const settings = useSelector(getSettings);
    const appointmentInstallationUrl = settings?.find(
        ({ settingKey }) => settingKey === SettingsKey.APPOINTMENT_INSTALLATION_URL,
    )?.settingValue;
    const appointmentDefaultContextTypeSetting = settings?.find(
        ({ settingKey }) => settingKey === SettingsKey.APPOINTMENT_DEFAULT_CONTEXT_TYPE,
    );
    const appointmentDefaultContextType = appointmentDefaultContextTypeSetting !== undefined 
        ? appointmentDefaultContextTypeSetting.settingValue 
        : 'TestDrive';
    const domain = useMemo(() => {
        const baseUrl = appointmentInstallationUrl || `${config.appointment.baseUrl}/${organization.id}`;
        return new URL(baseUrl);
    }, [appointmentInstallationUrl, organization]);
    const sharingOptions = template?.sharingOptions || [];
    const templateTypes = useSelector(getTemplateTypes);

    const type = templateTypes.find((t) => t.id === template.typeId);

    const getSharingParams = useCallback(
        (baseParams) => {
            if (type?.sharingParams) {
                Object.keys(type.sharingParams)?.forEach((key) => {
                    baseParams.set(key, type.sharingParams[key]);
                });
            }
            if (template?.sharingParams) {
                Object.keys(template.sharingParams)?.forEach((key) => {
                    baseParams.set(key, template.sharingParams[key]);
                });
            }
            return baseParams;
        },
        [type, template],
    );

    // Original URL with serviceId and dealer/group
    const originalSrc = useMemo(() => {
        let baseParams = new URLSearchParams({
            olOrgId: organization.id,
        });

        // Only add olInstContextType if it's not an empty string
        if (appointmentDefaultContextType && appointmentDefaultContextType !== '') {
            baseParams.set('olInstContextType', appointmentDefaultContextType);
        }

        // Only add olServiceType if type exists
        if (type) {
            baseParams.set('olServiceType', type.slug);
        }

        baseParams.set('olServiceId', template.id);

        // Include dealer or dealerGroup
        if (group?.metadata?.dealerId) {
            baseParams.set('olDealerId', group.metadata.dealerId);
        } else if (group?.metadata?.dealerGroupId) {
            baseParams.set('olDealerGroupId', group.metadata.dealerGroupId);
        }

        baseParams = getSharingParams(baseParams);
        return `${domain}?${baseParams.toString()}`;
    }, [organization.id, appointmentDefaultContextType, type, template.id, group.metadata.dealerId, group.metadata.dealerGroupId, getSharingParams, domain]);

    // URL with type only (without serviceId)
    const typeOnlySrc = useMemo(() => {
        if (!type) return null;

        let baseParams = new URLSearchParams({
            olOrgId: organization.id,
            olServiceType: type.slug,
        });

        // Only add olInstContextType if it's not an empty string
        if (appointmentDefaultContextType && appointmentDefaultContextType !== '') {
            baseParams.set('olInstContextType', appointmentDefaultContextType);
        }

        baseParams = getSharingParams(baseParams);
        return `${domain}?${baseParams.toString()}`;
    }, [organization, type, domain, getSharingParams, appointmentDefaultContextType]);

    // URL with dealer only (without serviceId)
    const dealerOnlySrc = useMemo(() => {
        if (!group?.metadata?.dealerId && !group?.metadata?.dealerGroupId) return null;

        let baseParams = new URLSearchParams({
            olOrgId: organization.id,
        });

        // Only add olInstContextType if it's not an empty string
        if (appointmentDefaultContextType && appointmentDefaultContextType !== '') {
            baseParams.set('olInstContextType', appointmentDefaultContextType);
        }

        // Only add olServiceType if type exists
        if (type) {
            baseParams.set('olServiceType', type.slug);
        }

        if (group?.metadata?.dealerId) {
            baseParams.set('olDealerId', group.metadata.dealerId);
        } else if (group?.metadata?.dealerGroupId) {
            baseParams.set('olDealerGroupId', group.metadata.dealerGroupId);
        }

        baseParams = getSharingParams(baseParams);
        return `${domain}?${baseParams.toString()}`;
    }, [organization, group, type, domain, getSharingParams, appointmentDefaultContextType]);

    // Complete URL (the original)
    const src = originalSrc;

    const iframeCode = `
<iframe
  allow="autoplay; camera; microphone; display-capture; clipboard-write"
  id="onlive-iframe"
  width="100%"
  style="border: none; height: 80px"
  src="${src}"
>
</iframe>
`;

    const getOptionByLang = (option) => {
        const title = option.title?.[lang];
        const defaultTitle = option.title?.en;
        return (title || defaultTitle).toUpperCase();
    };

    return (
        <LayoutDetails className={classes.small}>
            <Stack direction="column" spacing={2} className={classes.container}>
                <Typography variant="onliveH5" fontWeight="600">
                    {t('service.serviceShare.title').toUpperCase()}
                </Typography>

                {/* Iframe code section */}
                <Box className={classes.linkSection}>
                    <Typography variant="body2" className={classes.linkTitle}>
                        Embed Code
                    </Typography>
                    <CopyToClipboard text={iframeCode} key={`action-copy-code`}>
                        <ActionButton
                            key={`action-copy-code`}
                            color="primary"
                            fitWidth
                            spinner={false}
                            onClick={(e) => {
                                e.preventDefault();
                                enqueueSnackbar(t('service.serviceShare.embedCodeCopied'), {
                                    variant: 'success',
                                });
                            }}
                        >
                            {t('service.serviceShare.embedCode')}
                        </ActionButton>
                    </CopyToClipboard>
                </Box>

                {/* Links section */}
                <Box className={classes.linkSection}>
                    <Typography variant="body2" className={classes.linkTitle}>
                        Share Links
                    </Typography>

                    <Stack direction="row" spacing={2} flexWrap="wrap">
                        {/* Original link copy button (with serviceId and dealer/group) */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 120 }}>
                            <CopyToClipboard text={originalSrc} key={`action-copy-original-link`}>
                                <ActionButton
                                    key={`action-copy-original-link`}
                                    color="primary"
                                    fitWidth
                                    spinner={false}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        enqueueSnackbar(t('service.serviceShare.linkCopied'), {
                                            variant: 'success',
                                        });
                                    }}
                                >
                                    {t('service.serviceShare.copyLink')}
                                </ActionButton>
                            </CopyToClipboard>
                        </Box>

                        {/* Type only link copy button (when type exists) */}
                        {typeOnlySrc && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 120 }}>
                                <CopyToClipboard text={typeOnlySrc} key={`action-copy-type-link`}>
                                    <ActionButton
                                        key={`action-copy-type-link`}
                                        color="secondary"
                                        fitWidth
                                        spinner={false}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            enqueueSnackbar(t('service.serviceShare.linkCopied'), {
                                                variant: 'success',
                                            });
                                        }}
                                    >
                                        {t('service.serviceShare.copyTypeLink')}
                                    </ActionButton>
                                </CopyToClipboard>
                            </Box>
                        )}

                        {/* Dealer only link copy button (when dealer exists) */}
                        {dealerOnlySrc && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 120 }}>
                                <CopyToClipboard text={dealerOnlySrc} key={`action-copy-dealer-link`}>
                                    <ActionButton
                                        key={`action-copy-dealer-link`}
                                        color="secondary"
                                        fitWidth
                                        spinner={false}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            enqueueSnackbar(t('service.serviceShare.linkCopied'), {
                                                variant: 'success',
                                            });
                                        }}
                                    >
                                        {t('service.serviceShare.copyDealerLink')}
                                    </ActionButton>
                                </CopyToClipboard>
                            </Box>
                        )}
                    </Stack>
                </Box>

                {sharingOptions?.map((option) => {
                    let baseParams = new URLSearchParams();

                    // Only add olServiceType if type exists
                    if (type) {
                        baseParams.set('olServiceType', type.slug);
                    }

                    baseParams = getSharingParams(baseParams);
                    const url = `${option.url}&${baseParams.toString()}`;

                    return (
                        <>
                            <Divider />

                            <Stack direction="row" spacing={2} alignItems="center">
                                <Typography variant="subtitle1" fontWeight="500">
                                    {getOptionByLang(option)}
                                </Typography>
                                <CustomTooltip
                                    key={`action-share-${option.url}`}
                                    arrow
                                    placement="bottom"
                                    title={t('actions.share')}
                                    aria-label="share"
                                >
                                    <CopyToClipboard text={url} key={`action-share-${option.url}`}>
                                        <CustomIconButton
                                            aria-label="copy to share"
                                            size="small"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                enqueueSnackbar(t('service.serviceShare.iframeCopied'), {
                                                    variant: 'success',
                                                });
                                            }}
                                        >
                                            <ContentCopyOutlinedIcon />
                                        </CustomIconButton>
                                    </CopyToClipboard>
                                </CustomTooltip>
                            </Stack>
                        </>
                    );
                })}
            </Stack>
        </LayoutDetails>
    );
};

export default ServiceShare;
